import { useState, useMemo } from 'react';
import { tools } from '@/data/tools';
import { getFeaturedTools } from '@/lib/featured-utils';
import Header from './Header';
import Hero from './Hero';
import SearchBar from './SearchBar';
import CategoryGrid from './CategoryGrid';
import ToolGrid from './ToolGrid';
import ToolCounter from './ToolCounter';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { WebsiteStructuredData } from './StructuredData';
import SEOSections from './SEOSections';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Featured tools - Curated list of popular and new utilities
  // Dynamic selection that changes every 48 hours
  const featuredTools = useMemo(() => getFeaturedTools(tools), []);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <>
      <SEOHead
        title="Vibe Tools: Precision Developer Utilities & Web Toolkit"
        description="Access free, client-side web tools for developer, converter, design, and math tasks. Process all data locally in the browser with zero server uploads."
        keywords="vibe tools, precision tools, developer suite, web tools, fast utilities, free online tools, developer tools, image compressor, pdf editor, code formatter, sql formatter, regex tester, json formatter, css tools, text tools, productivity tools, digital design tools, ai tools, free utilities"
        isHomepage={true}
      />
      <WebsiteStructuredData />
      <main className="flex-grow relative z-10 transition-colors duration-500">
        <Hero />

        <CategoryGrid />

        <div className="container mx-auto px-4 py-12 md:py-20 max-w-[1240px]">

            <div className="flex justify-between items-end mb-10 md:mb-16">
              <div>
                <h2 className="text-3xl md:text-5xl font-bold mb-3 text-gray-900 dark:text-white tracking-tight">Featured Tools</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm md:text-lg font-medium">Top-rated utilities for your daily workflow.</p>
              </div>
              <Link
                to="/all-tools"
                className="hidden md:flex items-center gap-2 text-primary font-bold hover:text-primary/90 transition-colors"
              >
                View all tools <ArrowRight size={20} />
              </Link>
            </div>

            <ToolGrid tools={featuredTools} />

            <div className="mt-12 md:mt-24 text-center">
              <Link
                to="/all-tools"
                className="inline-flex items-center gap-2 px-10 py-4 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-white/10 rounded-full font-bold hover:bg-gray-50 dark:hover:bg-slate-800 hover:border-gray-300 dark:hover:border-white/20 transition-all shadow-lg active:scale-95"
              >
                Explore All Tools
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>

          <SEOSections />

      </main>
    </>
  );
}

export default Home;
